import React, { useState, useEffect, useCallback } from 'react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import { backupService, settingsService, systemService, systemLogsService, openaiCostsService } from '../../services/api';
import { PALETAS, aplicarPaleta, logoUrl, notificarBranding } from '../../branding';
import ConsultorioModal from '../ConsultorioModal';
import BotFlow from '../BotFlow';
import Bot from '../Bot';
import Nutricionistas from '../Nutricionistas';
import Substituicoes from '../Substituicoes';
import CardapiosProntos from '../CardapiosProntos';
import DistribuicaoRefeicoes from '../DistribuicaoRefeicoes';
import { Ico, emo } from '../ui/Ico';

// ── Estilos do design system para os cards desta tela ─────────────────────────
const SET_ICON = { fontSize:'1.5rem', width:48, height:48, borderRadius:'12px', background:'#f5ece2', color:'#5a3e2b',
  display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 };
const SET_BTN = { padding:'0.6rem 1.25rem', background:'#ffffff', color:'#2f2a26', border:'1px solid #d9d0c6',
  borderRadius:'9px', cursor:'pointer', fontWeight:600, fontSize:'0.875rem', flexShrink:0, whiteSpace:'nowrap' };
const SET_PANEL = { background:'#ffffff', border:'1px solid #ebe3da', borderRadius:'14px', padding:'1.25rem 1.5rem' };
function SettingsPanel({ icon, title, desc, children, initiallyOpen = false }) {
  const [open, setOpen] = useState(initiallyOpen);
  return (
    <section style={SET_PANEL}>
      <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '1rem' }}>
        <div style={SET_ICON}>{emo(icon)}</div>
        <div style={{ flex: '1 1 140px' }}>
          <p style={{ margin: '0 0 .25rem', fontWeight: 600 }}>{title}</p>
          <p style={{ margin: 0, fontSize: '.83rem', color: '#4a4440' }}>{desc}</p>
        </div>
        <button type="button" aria-expanded={open} style={SET_BTN} onClick={() => setOpen(!open)}>
          {open ? 'Fechar ↑' : 'Personalizar →'}
        </button>
      </div>
      {open && <div style={{ marginTop: '1.25rem', paddingTop: '1.25rem', borderTop: '1px solid #ebe3da', minWidth: 0 }}>
        {typeof children === 'function' ? children(() => setOpen(false)) : children}
      </div>}
    </section>
  );
}

const SERVICE_LABELS = {
  vision: 'Análise de Exame',
  diet_ai: 'Plano IA',
  resumo_clinico: 'Resumo Clínico',
  openai: 'OpenAI',
};
const fmtUSD = (v) => '$' + (Number(v) || 0).toFixed(Number(v) < 1 ? 4 : 2);
// Conversão para reais (cotação manual; futuramente plugável a uma API de câmbio)
const fmtBRL = (usd, cotacao) =>
  'R$ ' + ((Number(usd) || 0) * (Number(cotacao) || 0)).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const BRAND  = '#D4A574';

const NAV_SECOES = [
  {
    view:  'bot',
    icon:  '🤖',
    title: 'Bot WhatsApp',
    desc:  'Configure e monitore o bot de atendimento automático via WhatsApp. Veja o status da conexão e gerencie as conversas.',
  },
  {
    view:  'nutricionistas',
    icon:  '👩‍⚕️',
    title: 'Nutricionistas',
    desc:  'Gerencie as contas das nutricionistas cadastradas no sistema, crie novos acessos e defina permissões.',
  },
  {
    view:  'substituicoes',
    icon:  '🔄',
    title: 'Substituições de Alimentos',
    desc:  'Gerencie grupos de alimentos equivalentes para criar planos alimentares flexíveis. Listas por categoria e grupos de substituição calórica.',
  },
  {
    view:  'cardapios',
    icon:  '📋',
    title: 'Cardápios Prontos',
    desc:  'Biblioteca de modelos de plano por condição e meta calórica (idosos, diabetes, hipertensão, vegetariano…). Aplique no paciente com 1 clique e salve seus próprios modelos.',
  },
  {
    view:  'distribuicao-refeicoes',
    icon:  '🍽️',
    title: 'Distribuição de refeições',
    desc:  'Percentual das calorias do dia em cada refeição, por programa e número de refeições. É o ponto de partida do gerador de planos.',
  },
];

function fmtDate(s) {
  if (!s) return '—';
  return new Date(s).toLocaleString('pt-BR', { day:'2-digit', month:'2-digit', year:'numeric', hour:'2-digit', minute:'2-digit' });
}

// ── Card de Backup ─────────────────────────────────────────────────────────────
function BackupCard() {
  const [backups,   setBackups]   = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [running,   setRunning]   = useState(false);
  const [lastResult, setLastResult] = useState(null);
  const [error,     setError]     = useState('');

  const loadBackups = useCallback(async () => {
    try {
      const r = await backupService.list();
      if (r.success) setBackups(r.data.backups || []);
    } catch { setError('Não foi possível listar os backups.'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadBackups(); }, [loadBackups]);

  const handleBackup = async () => {
    setRunning(true); setError(''); setLastResult(null);
    try {
      const r = await backupService.run();
      if (r.success) {
        setLastResult(r.data);
        loadBackups();
      } else setError(r.error || 'Erro ao executar backup');
    } catch { setError('Erro de conexão ao executar backup.'); }
    finally { setRunning(false); }
  };

  const last = backups[0];

  return (
    <div>
      <div>
        <button
          onClick={handleBackup} disabled={running}
          style={{ padding:'0.6rem 1.25rem', background:'#ffffff', color:'#2f2a26', border:'1px solid #d9d0c6',
            borderRadius:'9px', cursor: running ? 'not-allowed' : 'pointer', fontWeight:600,
            fontSize:'0.875rem', flexShrink:0, whiteSpace:'nowrap', opacity: running ? 0.7 : 1 }}
        >
          {emo(running ? '⏳ Executando...' : '🔄 Fazer backup agora')}
        </button>
      </div>

      {/* Status */}
      <div style={{ marginTop:'1rem', display:'flex', gap:'1rem', flexWrap:'wrap', alignItems:'center' }}>
        <span style={{ fontSize:'0.78rem', background:'#edf1f6', color:'#4f6a86', borderRadius:'20px', padding:'2px 10px', fontWeight:600 }}>
          <Ico e="✅" sp />Backup automático ativo (diário às 02:00)
        </span>
        {last && (
          <span style={{ fontSize:'0.78rem', color:'#4a4440' }}>
            Último: <strong>{fmtDate(last.created_at)}</strong> · {last.size_mb} MB
          </span>
        )}
        {loading && <span style={{ fontSize:'0.78rem', color:'#736b64' }}>Carregando...</span>}
      </div>

      {lastResult && (
        <div style={{ marginTop:'0.75rem', padding:'0.6rem 0.9rem', background:'#edf4ee', borderRadius:'8px', fontSize:'0.82rem', color:'#2f5236' }}>
          <Ico e="✅" sp />Backup concluído: <strong>{lastResult.filename}</strong> — {lastResult.size_mb} MB · {lastResult.tables_count} tabelas · {lastResult.duration_seconds}s
        </div>
      )}

      {error && (
        <div style={{ marginTop:'0.75rem', padding:'0.6rem 0.9rem', background:'#f8ebe9', borderRadius:'8px', fontSize:'0.82rem', color:'#a8483f' }}>
          {error}
        </div>
      )}

      {/* Histórico dos últimos 5 backups */}
      {backups.length > 0 && (
        <div style={{ marginTop:'1rem' }}>
          <p style={{ margin:'0 0 0.4rem', fontSize:'0.75rem', fontWeight:600, color:'#4a4440', textTransform:'uppercase', letterSpacing:'.05em' }}>
            Histórico recente
          </p>
          <div style={{ display:'flex', flexDirection:'column', gap:'0.3rem' }}>
            {backups.slice(0, 5).map((b, i) => (
              <div key={i} style={{ display:'flex', justifyContent:'space-between', alignItems:'center',
                padding:'0.4rem 0.6rem', background:'rgba(255,255,255,0.7)', borderRadius:'6px', fontSize:'0.78rem' }}>
                <span style={{ color:'#4f6a86', fontFamily:'monospace' }}>{b.filename}</span>
                <span style={{ color:'#736b64', marginLeft:'0.5rem', flexShrink:0 }}>{b.size_mb} MB · {fmtDate(b.created_at)}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Card Minha Marca (white-label) ─────────────────────────────────────────────
function MinhaMarcaCard() {
  const [expanded, setExpanded] = useState(false);
  const [saving,   setSaving]   = useState(false);
  const [saved,    setSaved]    = useState(false);
  const [err,      setErr]      = useState('');
  const [enviandoLogo, setEnviandoLogo] = useState(false);
  const [marca, setMarca] = useState({
    business_name: '', crn: '',
    color_palette: 'terra', logo_path: '', logo_menu_scale: 100, logo_report_scale: 100,
  });

  useEffect(() => {
    settingsService.get().then(r => {
      if (r.success && r.data) {
        setMarca(prev => ({
          ...prev,
          business_name: r.data.business_name || '',
          crn:           r.data.crn           || '',
          color_palette: r.data.color_palette || 'terra',
          logo_path:     r.data.logo_path     || '',
          logo_menu_scale: Number(r.data.logo_menu_scale) || 100,
          logo_report_scale: Number(r.data.logo_report_scale) || 100,
        }));
      }
    }).catch(() => {});
  }, []);

  const set = (k, v) => setMarca(prev => ({ ...prev, [k]: v }));

  const handleSave = async () => {
    setSaving(true); setErr(''); setSaved(false);
    try {
      const r = await settingsService.save({
        business_name: marca.business_name, crn: marca.crn,
        color_palette: marca.color_palette,
        logo_menu_scale: marca.logo_menu_scale, logo_report_scale: marca.logo_report_scale,
      });
      if (r.success) {
        setSaved(true);
        aplicarPaleta(marca.color_palette);
        notificarBranding({ palette: marca.color_palette, logo_path: marca.logo_path, business_name: marca.business_name, logo_menu_scale: marca.logo_menu_scale, logo_report_scale: marca.logo_report_scale });
      } else setErr(r.error || 'Erro ao salvar');
    } catch { setErr('Erro de conexão'); }
    finally { setSaving(false); }
  };

  const handleLogo = async (file) => {
    if (!file) return;
    setEnviandoLogo(true); setErr('');
    try {
      const r = await settingsService.uploadLogo(file);
      if (r.success) {
        set('logo_path', r.data.logo_path);
        notificarBranding({ palette: marca.color_palette, logo_path: r.data.logo_path, business_name: marca.business_name, logo_menu_scale: marca.logo_menu_scale, logo_report_scale: marca.logo_report_scale });
      } else setErr(r.error || 'Erro no upload da logo');
    } catch (e) { setErr(e.response?.data?.error || 'Erro no upload da logo'); }
    finally { setEnviandoLogo(false); }
  };

  const inputStyle = {
    width:'100%', padding:'0.45rem 0.75rem', boxSizing:'border-box',
    border:'1px solid #d9d0c6', borderRadius:'8px', fontSize:'0.875rem', color:'#2f2a26', background:'#ffffff',
  };
  const labelStyle = { fontSize:'0.73rem', fontWeight:600, color:'#4a4440', textTransform:'uppercase', letterSpacing:'.04em', marginBottom:'0.2rem', display:'block' };

  return (
    <div style={SET_PANEL}>
      <div style={{ display:'flex', alignItems:'center', gap:'1.25rem' }}>
        <div style={SET_ICON}>
          <Ico e="🎨" />
        </div>
        <div style={{ flex:1 }}>
          <p style={{ margin:'0 0 0.2rem', fontSize:'1rem', fontWeight:600, color:'#2f2a26' }}>Minha Marca</p>
          <p style={{ margin:0, fontSize:'0.83rem', color:'#4a4440', lineHeight:1.5 }}>
            Logo, cores e dados profissionais que aparecem no sistema, nos PDFs enviados aos pacientes e nas cobranças.
          </p>
        </div>
        <button onClick={() => setExpanded(v => !v)}
          style={{ padding:'0.6rem 1.25rem', background:'#ffffff', color:'#2f2a26', border:'1px solid #d9d0c6',
            borderRadius:'9px', cursor:'pointer', fontWeight:600, fontSize:'0.875rem', flexShrink:0 }}>
          {expanded ? 'Fechar ↑' : 'Personalizar →'}
        </button>
      </div>

      {expanded && (
        <div style={{ marginTop:'1.25rem', borderTop:'1px solid #edf1f6', paddingTop:'1.25rem' }}>
          {/* Logo */}
          <div style={{ display:'flex', alignItems:'center', gap:'1rem', marginBottom:'1.25rem' }}>
            <div style={{ width:88, height:88, borderRadius:'12px', background:'#ffffff', border:'1px dashed #b8c7d8',
              display:'flex', alignItems:'center', justifyContent:'center', overflow:'hidden', flexShrink:0 }}>
              {marca.logo_path
                ? <img src={logoUrl(marca.logo_path)} alt="Logo" style={{ maxWidth:'100%', maxHeight:'100%', objectFit:'contain' }} />
                : <span style={{ fontSize:'0.7rem', color:'#736b64', textAlign:'center', padding:'0 6px' }}>Sem logo</span>}
            </div>
            <div>
              <label style={{ ...labelStyle, marginBottom:'0.4rem' }}>Logo da sua marca (PNG/JPG/WebP/SVG, até 2 MB)</label>
              <label style={{ display:'inline-block', padding:'0.5rem 1rem', background:'#ffffff', border:'1.5px solid #7189a6',
                color:'#7189a6', borderRadius:'8px', cursor:'pointer', fontWeight:600, fontSize:'0.83rem' }}>
                {enviandoLogo ? 'Enviando…' : (marca.logo_path ? 'Trocar logo' : 'Enviar logo')}
                <input type="file" accept="image/png,image/jpeg,image/webp,image/svg+xml" style={{ display:'none' }}
                  onChange={e => { handleLogo(e.target.files?.[0]); e.target.value = ''; }} />
              </label>
            </div>
          </div>

          <div style={{ marginBottom:'1.25rem' }}>
            {[['logo_menu_scale', 'Tamanho da logo no menu'], ['logo_report_scale', 'Tamanho da logo nos relatórios']].map(([field, label]) => (
              <div key={field} style={{ marginBottom:'1rem' }}>
                <label htmlFor={field} style={labelStyle}>{label} — {marca[field]}%</label>
                <div style={{ display:'flex', alignItems:'center', gap:'0.75rem' }}>
                  <input id={field} type="range" min="50" max="300" step="10" value={marca[field]}
                    onChange={e => { set(field, Number(e.target.value)); setSaved(false); }}
                    style={{ flex:1, accentColor:'var(--color-primary-strong)' }} />
                  <button type="button" onClick={() => { set(field, 100); setSaved(false); }}
                    style={{ ...inputStyle, width:'auto', cursor:'pointer' }}>100%</button>
                </div>
                <div style={{ marginTop:'0.5rem', padding:'0.75rem', background:'#ffffff', border:'1px solid #d9d0c6', borderRadius:8, overflow:'auto' }}>
                  {marca.logo_path ? <img src={logoUrl(marca.logo_path)} alt={`Prévia: ${label}`}
                    style={{ height: (field === 'logo_menu_scale' ? 42 : 54) * marca[field] / 100, maxWidth:'100%', objectFit:'contain', display:'block' }} />
                    : <span style={{ fontSize:'0.8rem', color:'#736b64' }}>Envie uma logo para visualizar.</span>}
                </div>
              </div>
            ))}
            <p style={{ fontSize:'0.8rem', color:'#736b64', margin:0 }}>Confira a prévia e clique em Salvar minha marca para aplicar. A imagem mantém sua proporção.</p>
          </div>

          {/* Paleta */}
          <label style={labelStyle}>Paleta de cores do sistema</label>
          <div style={{ display:'flex', gap:'0.6rem', flexWrap:'wrap', marginBottom:'1.25rem' }}>
            {Object.entries(PALETAS).map(([key, p]) => (
              <button key={key} onClick={() => { set('color_palette', key); aplicarPaleta(key); }}
                style={{ display:'flex', alignItems:'center', gap:'0.45rem', padding:'0.45rem 0.8rem',
                  background:'#ffffff', borderRadius:'9px', cursor:'pointer', fontSize:'0.8rem', fontWeight:600,
                  border: marca.color_palette === key ? '2px solid #7189a6' : '1.5px solid #edf1f6',
                  color: marca.color_palette === key ? '#34495e' : '#4a4440' }}>
                <span style={{ width:16, height:16, borderRadius:'50%', background:p.primary, display:'inline-block' }} />
                <span style={{ width:16, height:16, borderRadius:'50%', background:p.dark, display:'inline-block', marginLeft:-10 }} />
                {emo(p.label)}
              </button>
            ))}
          </div>

          {/* Dados profissionais */}
          <div style={{ display:'grid', gridTemplateColumns:'2fr 1fr', gap:'0.75rem', marginBottom:'0.75rem' }}>
            <div>
              <label style={labelStyle}>Nome da marca / clínica</label>
              <input style={inputStyle} value={marca.business_name} onChange={e => set('business_name', e.target.value)}
                placeholder="Ex.: Clínica NutriVida" />
            </div>
            <div>
              <label style={labelStyle}>CRN</label>
              <input style={inputStyle} value={marca.crn} onChange={e => set('crn', e.target.value)}
                placeholder="CRN-4 12345" />
            </div>
          </div>
          {/* Banco/agência/conta e PIX foram removidos daqui — não fazem parte da
              identidade da marca. PIX é configurado na tela do Bot (WhatsApp). */}

          <div style={{ display:'flex', gap:'0.75rem', alignItems:'center' }}>
            <button onClick={handleSave} disabled={saving}
              style={{ padding:'0.6rem 1.5rem', background:'var(--color-primary-strong)', color:'#ffffff', border:'none',
                borderRadius:'8px', cursor: saving ? 'not-allowed' : 'pointer', fontWeight:600, fontSize:'0.875rem', opacity: saving ? 0.7 : 1 }}>
              {emo(saving ? 'Salvando...' : '💾 Salvar minha marca')}
            </button>
            {saved && <span style={{ fontSize:'0.83rem', color:'#46714f', fontWeight:600 }}><Ico e="✅" sp />Salvo!</span>}
            {err   && <span style={{ fontSize:'0.83rem', color:'#c46a60' }}>{err}</span>}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Card de E-mail SMTP ────────────────────────────────────────────────────────
function EmailSmtpCard() {
  const [expanded, setExpanded] = useState(false);
  const [saving,   setSaving]   = useState(false);
  const [saved,    setSaved]    = useState(false);
  const [err,      setErr]      = useState('');
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState('');
  const [smtp, setSmtp] = useState({
    smtp_host: '', smtp_port: 587, smtp_user: '', smtp_password: '',
    smtp_from_email: '', smtp_from_name: '', smtp_enabled: false,
  });

  useEffect(() => {
    settingsService.get().then(r => {
      if (r.success && r.data) {
        setSmtp(prev => ({
          ...prev,
          smtp_host:       r.data.smtp_host       || '',
          smtp_port:       r.data.smtp_port        || 587,
          smtp_user:       r.data.smtp_user        || '',
          smtp_password:   r.data.smtp_password    || '',
          smtp_from_email: r.data.smtp_from_email  || '',
          smtp_from_name:  r.data.smtp_from_name   || '',
          smtp_enabled:    r.data.smtp_enabled     || false,
        }));
      }
    }).catch(() => {});
  }, []);

  const set = (k, v) => setSmtp(prev => ({ ...prev, [k]: v }));

  const handleSave = async () => {
    setSaving(true); setErr(''); setSaved(false);
    try {
      const r = await settingsService.save(smtp);
      if (r.success) { setSaved(true); if (smtp.smtp_password) set('smtp_password', '••••••••'); }
      else setErr(r.error || 'Erro ao salvar');
    } catch (e) { setErr(e.response?.data?.error || 'Erro de conexão'); }
    finally { setSaving(false); }
  };

  const testSmtp = async () => {
    setTesting(true); setErr(''); setTestResult('');
    try {
      const r = await settingsService.testSmtp();
      setTestResult(r.message || 'Conexão e autenticação verificadas.');
    } catch (e) {
      setErr(e.response?.data?.error || 'Falha ao testar SMTP.');
    } finally {
      setTesting(false);
    }
  };

  const inputStyle = {
    width:'100%', padding:'0.45rem 0.75rem', boxSizing:'border-box',
    border:'1px solid #d9d0c6', borderRadius:'8px', fontSize:'0.875rem', color:'#2f2a26', background:'#ffffff',
  };
  const labelStyle = { fontSize:'0.73rem', fontWeight:600, color:'#4a4440', textTransform:'uppercase', letterSpacing:'.04em', marginBottom:'0.2rem', display:'block' };

  return (
    <div style={SET_PANEL}>
      <div style={{ display:'flex', alignItems:'center', gap:'1.25rem' }}>
        <div style={SET_ICON}>
          <Ico e="📧" />
        </div>
        <div style={{ flex:1 }}>
          <p style={{ margin:'0 0 0.2rem', fontSize:'1rem', fontWeight:600, color:'#2f2a26' }}>E-mail SMTP</p>
          <p style={{ margin:0, fontSize:'0.83rem', color:'#4a4440', lineHeight:1.5 }}>
            Conecte a sua conta de e-mail para enviar relatórios e planos alimentares.
          </p>
        </div>
        <button onClick={() => setExpanded(v => !v)}
          style={{ padding:'0.6rem 1.25rem', background:'#ffffff', color:'#2f2a26', border:'1px solid #d9d0c6',
            borderRadius:'9px', cursor:'pointer', fontWeight:600, fontSize:'0.875rem', flexShrink:0 }}>
          {expanded ? 'Fechar ↑' : 'Personalizar →'}
        </button>
      </div>

      {expanded && (
        <div style={{ marginTop:'1.25rem', borderTop:'1px solid #e8c9a8', paddingTop:'1.25rem' }}>
          <div style={{ display:'flex', alignItems:'center', gap:'0.6rem', marginBottom:'1rem' }}>
            <label style={{ display:'flex', alignItems:'center', gap:'0.4rem', cursor:'pointer', fontSize:'0.875rem', fontWeight:600, color:'#5a3e2b' }}>
              <input type="checkbox" checked={!!smtp.smtp_enabled} onChange={e => set('smtp_enabled', e.target.checked)}
                style={{ accentColor:'#c4933f', width:16, height:16 }} />
              Ativar envio via SMTP
            </label>
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'2fr 1fr', gap:'0.75rem', marginBottom:'0.75rem' }}>
            <div>
              <label style={labelStyle}>Servidor SMTP (host)</label>
              <input style={inputStyle} value={smtp.smtp_host} onChange={e => set('smtp_host', e.target.value)}
                placeholder="smtp.gmail.com" />
            </div>
            <div>
              <label style={labelStyle}>Porta</label>
              <select style={inputStyle} value={smtp.smtp_port} onChange={e => set('smtp_port', Number(e.target.value))}>
                {[587, 465, 2525, 25].map(port => <option key={port} value={port}>{port}</option>)}
              </select>
            </div>
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'0.75rem', marginBottom:'0.75rem' }}>
            <div>
              <label style={labelStyle}>Usuário / E-mail da conta</label>
              <input style={inputStyle} value={smtp.smtp_user} onChange={e => set('smtp_user', e.target.value)}
                placeholder="seumail@gmail.com" />
            </div>
            <div>
              <label style={labelStyle}>Senha / App Password</label>
              <input style={inputStyle} type="password" value={smtp.smtp_password} onChange={e => set('smtp_password', e.target.value)}
                placeholder="Senha ou App Password" />
            </div>
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'0.75rem', marginBottom:'1rem' }}>
            <div>
              <label style={labelStyle}>E-mail remetente (From)</label>
              <input style={inputStyle} value={smtp.smtp_from_email} onChange={e => set('smtp_from_email', e.target.value)}
                placeholder="contato@levnutri.com" />
            </div>
            <div>
              <label style={labelStyle}>Nome remetente</label>
              <input style={inputStyle} value={smtp.smtp_from_name} onChange={e => set('smtp_from_name', e.target.value)}
                placeholder="Ex.: Ana Souza — Nutrição" />
            </div>
          </div>
          <p style={{ fontSize:'0.72rem', color:'#8a5f1a', margin:'0 0 1rem', background:'#f8f1e2', padding:'0.5rem 0.75rem', borderRadius:'6px' }}>
            <Ico e="💡" sp />Use o servidor SMTP indicado pelo seu provedor. A porta 465 usa TLS direto. As portas 587, 2525 e 25 usam STARTTLS. Se o provedor exigir uma senha de aplicativo, informe-a no campo Senha.
          </p>
          <div style={{ display:'flex', flexWrap:'wrap', gap:'0.75rem', alignItems:'center' }}>
            <button onClick={handleSave} disabled={saving}
              style={{ padding:'0.6rem 1.5rem', background:'var(--color-primary-strong)', color:'#ffffff', border:'none',
                borderRadius:'8px', cursor: saving ? 'not-allowed' : 'pointer', fontWeight:600, fontSize:'0.875rem', opacity: saving ? 0.7 : 1 }}>
              {emo(saving ? 'Salvando...' : '💾 Salvar configurações SMTP')}
            </button>
            <button type="button" style={SET_BTN} onClick={testSmtp} disabled={testing || saving}>
              {testing ? 'Testando…' : 'Testar conexão salva'}
            </button>
            {testResult && <span role="status">{testResult}</span>}
            {saved && <span style={{ fontSize:'0.83rem', color:'#46714f', fontWeight:600 }}><Ico e="✅" sp />Salvo!</span>}
            {err   && <span style={{ fontSize:'0.83rem', color:'#c46a60' }}>{err}</span>}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Diagnóstico do Sistema ─────────────────────────────────────────────────────
const LOG_COLOR = { info: '#4f6a86', warning: '#8a5f1a', error: '#c46a60' };
const LOG_BG    = { info: '#edf1f6', warning: '#fbf8f5', error: '#f8ebe9' };
const SVC_ORDER = ['mysql', 'smtp', 'openai', 'fila', 'backup', 'documentos'];

function DiagnosticoView({ onBack }) {
  const [status,       setStatus]       = useState(null);
  const [loadingAll,   setLoadingAll]   = useState(true);
  const [testing,      setTesting]      = useState(null);
  const [logs,         setLogs]         = useState([]);
  const [logServices,  setLogServices]  = useState([]);
  const [loadingLogs,  setLoadingLogs]  = useState(true);
  const [logLevel,     setLogLevel]     = useState('');
  const [logService,   setLogService]   = useState('');
  const [clearingLogs, setClearingLogs] = useState(false);

  const loadAllStatus = useCallback(async () => {
    setLoadingAll(true);
    try {
      const r = await systemService.getStatus();
      if (r.success) setStatus(r.data);
    } catch { /* silencioso */ }
    setLoadingAll(false);
  }, []);

  const testOne = async (svc) => {
    setTesting(svc);
    try {
      const r = await systemService.testService(svc);
      if (r.success && r.data.statuses?.[svc]) {
        setStatus(prev => ({
          ...prev,
          statuses:   { ...(prev?.statuses || {}), [svc]: r.data.statuses[svc] },
          checked_at: r.data.checked_at,
        }));
      }
    } catch { /* silencioso */ }
    setTesting(null);
  };

  const loadLogs = useCallback(async () => {
    setLoadingLogs(true);
    try {
      const r = await systemLogsService.get({ level: logLevel, service: logService });
      if (r.success) { setLogs(r.data.logs || []); setLogServices(r.data.services || []); }
    } catch { /* silencioso */ }
    setLoadingLogs(false);
  }, [logLevel, logService]);

  useEffect(() => { loadAllStatus(); }, [loadAllStatus]);
  useEffect(() => { loadLogs(); }, [loadLogs]);

  const clearLogs = async () => {
    setClearingLogs(true);
    try { await systemLogsService.clear(logService); loadLogs(); } catch { /* silencioso */ }
    setClearingLogs(false);
  };

  const sel = { padding:'0.3rem 0.5rem', border:'1px solid #d9d0c6', borderRadius:'6px', fontSize:'0.78rem', background:'#ffffff', color:'#2f2a26' };
  const miniBtn = (color, bg, border) => ({
    padding:'0.3rem 0.65rem', background: bg, border:`1px solid ${border}`,
    borderRadius:'6px', fontSize:'0.72rem', cursor:'pointer', color,
  });

  return (
    <div>
      <button onClick={onBack} style={{ background:'none', border:'none', cursor:'pointer',
        color:BRAND, fontSize:'0.875rem', padding:'0', marginBottom:'1.5rem',
        display:'flex', alignItems:'center', gap:'0.3rem', fontWeight:600 }}>
        ← Voltar às Configurações
      </button>

      <h1 style={{ margin:'0 0 0.2rem', fontSize:'1.5rem', fontWeight:700, color:'#2f2a26' }}>
        <Ico e="🔍" sp />Diagnóstico do Sistema
      </h1>
      <p style={{ margin:'0 0 2rem', fontSize:'0.875rem', color:'#736b64' }}>
        Status dos serviços, logs em tempo real e informações técnicas
        {status?.checked_at && ` · ${new Date(status.checked_at).toLocaleTimeString('pt-BR')}`}
      </p>

      <div style={{ display:'flex', flexDirection:'column', gap:'1.5rem', maxWidth:680 }}>

        {/* SEÇÃO 1 — Status dos Serviços */}
        <section>
          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:'0.65rem' }}>
            <h2 style={{ margin:0, fontSize:'1rem', fontWeight:600, color:'#2f2a26' }}>Status dos Serviços</h2>
            <button onClick={loadAllStatus} disabled={loadingAll} style={miniBtn('#736b64','#fbf8f5','#d9d0c6')}>
              {emo(loadingAll ? '⏳ Verificando...' : '↻ Atualizar tudo')}
            </button>
          </div>
          <div style={{ display:'flex', flexDirection:'column', gap:'0.45rem' }}>
            {loadingAll && !status ? (
              <div style={{ color:'#736b64', fontSize:'0.875rem', textAlign:'center', padding:'1.5rem' }}>Verificando serviços...</div>
            ) : (
              SVC_ORDER.map(key => {
                const s = status?.statuses?.[key];
                if (!s) return null;
                return (
                  <div key={key} style={{ display:'flex', alignItems:'center', gap:'0.75rem', padding:'0.65rem 1rem',
                    background: s.ok ? '#edf4ee' : '#f8ebe9',
                    borderRadius:'10px', border:`1px solid ${s.ok ? '#b9d2bd' : '#f8ebe9'}` }}>
                    <span style={{ fontSize:'1.1rem' }}>{emo(s.ok ? '✅' : '❌')}</span>
                    <div style={{ flex:1, minWidth:0 }}>
                      <div style={{ fontWeight:600, fontSize:'0.875rem', color:'#2f2a26' }}>{emo(s.label)}</div>
                      {s.detail && (
                        <div style={{ fontSize:'0.73rem', color:'#4a4440', marginTop:'1px', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                          {s.detail}
                        </div>
                      )}
                    </div>
                    {s.ms > 0 && <span style={{ fontSize:'0.7rem', color:'#736b64', flexShrink:0 }}>{s.ms}ms</span>}
                    <button onClick={() => testOne(key)} disabled={testing === key}
                      style={{ ...miniBtn('#4a4440','#ffffff','#d9d0c6'), flexShrink:0, opacity: testing === key ? 0.5 : 1 }}>
                      {testing === key ? '...' : '↻ Testar'}
                    </button>
                  </div>
                );
              })
            )}
          </div>
        </section>

        {/* SEÇÃO 2 — Logs */}
        <section>
          <div style={{ display:'flex', alignItems:'center', gap:'0.6rem', marginBottom:'0.65rem', flexWrap:'wrap' }}>
            <h2 style={{ margin:0, fontSize:'1rem', fontWeight:600, color:'#2f2a26', marginRight:'auto' }}>Logs Recentes</h2>
            <select value={logLevel} onChange={e => setLogLevel(e.target.value)} style={sel}>
              <option value="">Todos os níveis</option>
              <option value="info">INFO</option>
              <option value="warning">WARNING</option>
              <option value="error">ERROR</option>
            </select>
            <select value={logService} onChange={e => setLogService(e.target.value)} style={sel}>
              <option value="">Todos os serviços</option>
              {logServices.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
            <button onClick={clearLogs} disabled={clearingLogs}
              style={miniBtn('#c46a60','#f8ebe9','#f8ebe9')}>
              {emo(clearingLogs ? '...' : '🗑️ Limpar')}
            </button>
          </div>
          {loadingLogs ? (
            <div style={{ color:'#736b64', fontSize:'0.875rem', textAlign:'center', padding:'1rem' }}>Carregando logs...</div>
          ) : logs.length === 0 ? (
            <div style={{ color:'#736b64', fontSize:'0.875rem', textAlign:'center', padding:'1.5rem',
              background:'#fbf8f5', borderRadius:'10px', border:'1px dashed #d9d0c6' }}>
              Nenhum log encontrado
            </div>
          ) : (
            <div style={{ maxHeight:320, overflowY:'auto', border:'1px solid #d9d0c6', borderRadius:'10px', background:'#ffffff' }}>
              {logs.map(log => (
                <div key={log.id} style={{ display:'flex', gap:'0.5rem', padding:'0.45rem 0.75rem',
                  borderBottom:'1px solid #f6f0ea', alignItems:'flex-start',
                  background: log.level === 'error' ? '#f8ebe9' : log.level === 'warning' ? '#fbf8f5' : '#ffffff' }}>
                  <span style={{ fontSize:'0.66rem', fontWeight:600, color: LOG_COLOR[log.level],
                    background: LOG_BG[log.level], padding:'1px 5px', borderRadius:'3px',
                    flexShrink:0, marginTop:'2px', textTransform:'uppercase' }}>
                    {log.level}
                  </span>
                  <span style={{ fontSize:'0.72rem', color: BRAND, fontWeight:600, flexShrink:0, width:62 }}>
                    {log.service}
                  </span>
                  <span style={{ fontSize:'0.78rem', color:'#4a4440', flex:1, lineHeight:1.4 }}>{emo(log.message)}</span>
                  <span style={{ fontSize:'0.66rem', color:'#a39b93', flexShrink:0, fontFamily:'monospace', whiteSpace:'nowrap' }}>
                    {new Date(log.created_at).toLocaleString('pt-BR', { day:'2-digit', month:'2-digit', hour:'2-digit', minute:'2-digit', second:'2-digit' })}
                  </span>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* SEÇÃO 3 — Informações do Sistema */}
        {status?.sysInfo && !status.sysInfo.error && (
          <section>
            <h2 style={{ margin:'0 0 0.65rem', fontSize:'1rem', fontWeight:600, color:'#2f2a26' }}>Informações do Sistema</h2>
            <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(175px,1fr))', gap:'0.5rem' }}>
              {[
                { label:'PHP',             value:`v${status.sysInfo.php_version}` },
                { label:'MySQL',           value:`v${status.sysInfo.mysql_version}` },
                { label:'Disco livre',     value: status.sysInfo.disk_free_gb != null ? `${status.sysInfo.disk_free_gb} GB` : '—' },
                { label:'Último backup',   value: status.sysInfo.last_backup || 'Nenhum' },
                { label:'Total backups',   value: String(status.sysInfo.backup_count) },
                { label:'Pacientes',       value: String(status.sysInfo.counts?.pacientes ?? '—') },
                { label:'Planos gerados',  value: String(status.sysInfo.counts?.planos ?? '—') },
                { label:'Consultas',       value: String(status.sysInfo.counts?.consultas ?? '—') },
              ].map(({ label, value }) => (
                <div key={label} style={{ background:'#fbf8f5', borderRadius:'8px', padding:'0.6rem 0.9rem', border:'1px solid #ebe3da' }}>
                  <div style={{ fontSize:'0.7rem', color:'#736b64', textTransform:'uppercase', letterSpacing:'.04em', marginBottom:'3px' }}>{emo(label)}</div>
                  <div style={{ fontWeight:600, fontSize:'0.9rem', color:'#2f2a26', fontFamily:'monospace' }}>{value}</div>
                </div>
              ))}
            </div>
          </section>
        )}
      </div>
    </div>
  );
}

// ── Painel de Custos OpenAI ────────────────────────────────────────────────────
function CustosIAView({ onBack }) {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr]         = useState('');
  const [limit, setLimit]     = useState('20');
  const [pause, setPause]     = useState(false);
  const [notify, setNotify]   = useState(false);
  const [dailyThr, setDailyThr] = useState('2');
  const [cotacao, setCotacao]   = useState('5.40');
  const [savingCfg, setSavingCfg] = useState(false);
  const [savedCfg, setSavedCfg]   = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setErr('');
    try {
      const r = await openaiCostsService.get();
      if (r.success) {
        setData(r.data);
        const c = r.data.config || {};
        setLimit(String(c.monthly_limit ?? 20));
        setPause(!!c.pause_on_limit);
        setNotify(!!c.notify_daily);
        setDailyThr(String(c.daily_threshold ?? 2));
        setCotacao(String(c.cotacao_dolar ?? r.data.cotacao_dolar ?? 5.40));
      } else setErr(r.error || 'Erro ao carregar custos');
    } catch (e) { setErr('Erro ao carregar custos'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const saveCfg = async () => {
    setSavingCfg(true); setSavedCfg(false);
    try {
      await openaiCostsService.saveSettings({
        monthly_limit: parseFloat(limit) || 0,
        pause_on_limit: pause,
        notify_daily: notify,
        daily_threshold: parseFloat(dailyThr) || 0,
        cotacao_dolar: parseFloat(cotacao) || 0,
      });
      setSavedCfg(true); setTimeout(() => setSavedCfg(false), 2500);
      load();
    } catch { setErr('Erro ao salvar configurações'); }
    finally { setSavingCfg(false); }
  };

  const a       = data?.alertas || {};
  const pct     = Math.min(100, a.pct_limite || 0);
  const barColor = a.mes_acima ? '#c46a60' : a.mes_perto ? '#c4933f' : '#46714f';
  const cot     = parseFloat(cotacao) || data?.cotacao_dolar || 5.40;

  return (
    <div style={{ maxWidth: '720px' }}>
      <button onClick={onBack} style={{ background:'none', border:'none', cursor:'pointer',
        color:'#736b64', fontSize:'0.9rem', marginBottom:'1rem', padding:0 }}>← Voltar</button>
      <h1 style={{ margin:'0 0 0.25rem', fontSize:'1.5rem', fontWeight:700, color:'#2f2a26' }}><Ico e="💰" sp />Custos OpenAI</h1>
      <p style={{ margin:'0 0 1.5rem', fontSize:'0.875rem', color:'#736b64' }}>Monitoramento de uso e gastos da inteligência artificial</p>

      {loading && <p style={{ color:'#736b64' }}>Carregando...</p>}
      {err && <div style={{ background:'#f8ebe9', border:'1px solid #e3b3ad', color:'#a8483f', padding:'0.75rem 1rem', borderRadius:'10px', marginBottom:'1rem' }}><Ico e="⚠" /> {err}</div>}

      {data && (
        <>
          <section style={{ ...SET_PANEL, marginBottom: '1rem' }}>
            <h2 style={{ marginTop: 0, fontSize: '1rem' }}>Cobrança oficial do projeto OpenAI</h2>
            {data.cobranca?.status === 'ok' ? (
              <>
                <p>Hoje {fmtUSD(data.cobranca.totais.hoje)} · Últimos 7 dias {fmtUSD(data.cobranca.totais.sete_dias)} · Mês {fmtUSD(data.cobranca.totais.mes)}</p>
                <small>Períodos em UTC · Atualizado em {new Date(data.cobranca.checked_at).toLocaleString('pt-BR')}</small>
              </>
            ) : <p>{data.cobranca?.message || 'Cobrança oficial indisponível.'}</p>}
          </section>
          <h2 style={{ fontSize: '1rem' }}>Estimativas locais por uso registrado</h2>
          <p style={{ fontSize: '.83rem' }}>Os detalhes por paciente e os alertas de limite usam os registros locais de consumo.</p>
          {/* Banner de alerta */}
          {a.mes_acima && (
            <div style={{ background:'#f8ebe9', border:'1.5px solid #c46a60', color:'#a8483f', padding:'0.85rem 1rem', borderRadius:'12px', marginBottom:'1rem', fontWeight:600 }}>
              <Ico e="🚨" sp />Limite mensal de {fmtUSD(data.config.monthly_limit)} ({fmtBRL(data.config.monthly_limit, cot)}) ultrapassado! Estimativa atual: {fmtUSD(data.mes.custo)} ({fmtBRL(data.mes.custo, cot)}).
              {data.config.pause_on_limit ? ' Análises de exame estão pausadas.' : ''}
            </div>
          )}
          {!a.mes_acima && a.mes_perto && (
            <div style={{ background:'#fbf8f5', border:'1.5px solid #c4933f', color:'#8a5f1a', padding:'0.85rem 1rem', borderRadius:'12px', marginBottom:'1rem', fontWeight:600 }}>
              <Ico e="⚠" sp />Você já usou {a.pct_limite}% do limite mensal ({fmtUSD(data.mes.custo)} · {fmtBRL(data.mes.custo, cot)} de {fmtUSD(data.config.monthly_limit)}).
            </div>
          )}

          {/* Card resumo */}
          <div style={{ background:'#ffffff', border:'1.5px solid #ebe3da', borderRadius:'14px', padding:'1.25rem 1.5rem', marginBottom:'1.25rem', boxShadow:'0 2px 8px rgba(0,0,0,0.04)' }}>
            <div style={{ display:'flex', gap:'1.5rem', flexWrap:'wrap', marginBottom:'1rem' }}>
              {[['Hoje', data.hoje.custo, `${data.hoje.requests} req`],
                ['7 dias', data.sete_dias.custo, ''],
                ['Mês', data.mes.custo, `${data.mes.requests} req · ${(data.mes.tokens/1000).toFixed(0)}k tokens`]].map(([l, v, sub]) => (
                <div key={l} style={{ flex:'1 1 120px' }}>
                  <div style={{ fontSize:'0.78rem', color:'#736b64', marginBottom:'2px' }}>{l}</div>
                  <div style={{ fontSize:'1.5rem', fontWeight:700, color:'#2f2a26' }}>{fmtUSD(v)}</div>
                  <div style={{ fontSize:'0.8rem', color:'#46714f', fontWeight:600 }}>{fmtBRL(v, cot)}</div>
                  {sub && <div style={{ fontSize:'0.72rem', color:'#a39b93' }}>{sub}</div>}
                </div>
              ))}
            </div>
            <div style={{ fontSize:'0.8rem', color:'#4a4440', marginBottom:'0.35rem' }}>
              Limite mensal: {fmtUSD(data.config.monthly_limit)} ({fmtBRL(data.config.monthly_limit, cot)}) <strong>· {a.pct_limite}%</strong>
            </div>
            <div style={{ height:'12px', background:'#f6f0ea', borderRadius:'6px', overflow:'hidden' }}>
              <div style={{ width:`${pct}%`, height:'100%', background:barColor, transition:'width .3s' }} />
            </div>
          </div>

          {/* Gráfico 7 dias */}
          <div style={{ background:'#ffffff', border:'1.5px solid #ebe3da', borderRadius:'14px', padding:'1.25rem 1rem 0.5rem', marginBottom:'1.25rem' }}>
            <p style={{ margin:'0 0 0.75rem 0.5rem', fontSize:'0.9rem', fontWeight:600, color:'#2f2a26' }}>Custo por dia (últimos 7 dias)</p>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={data.sete_dias.por_dia} margin={{ top: 10, right: 8, left: 0, bottom: 0 }}>
                <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} domain={[0, (dataMax) => (dataMax > 0 ? dataMax * 1.2 : 0.01)]}
                  tickFormatter={(v) => '$' + Number(v).toFixed(2)} width={56} allowDecimals />
                <Tooltip formatter={(v) => [`${fmtUSD(v)} · ${fmtBRL(v, cot)}`, 'Custo']} labelStyle={{ color:'#2f2a26' }} />
                <Bar dataKey="custo" fill={BRAND} radius={[4,4,0,0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Por serviço + Top pacientes */}
          <div style={{ display:'flex', gap:'1rem', flexWrap:'wrap', marginBottom:'1.25rem' }}>
            <div style={{ flex:'1 1 280px', background:'#ffffff', border:'1.5px solid #ebe3da', borderRadius:'14px', padding:'1rem 1.25rem' }}>
              <p style={{ margin:'0 0 0.75rem', fontSize:'0.9rem', fontWeight:600, color:'#2f2a26' }}>Por serviço (mês)</p>
              {data.por_servico.length === 0 && <p style={{ color:'#a39b93', fontSize:'0.83rem' }}>Sem dados ainda.</p>}
              {data.por_servico.map(s => (
                <div key={s.service} style={{ display:'flex', justifyContent:'space-between', fontSize:'0.83rem', padding:'0.3rem 0', borderBottom:'1px solid #f6f0ea' }}>
                  <span>{emo(SERVICE_LABELS[s.service] || s.service)} <span style={{ color:'#a39b93' }}>· {s.requests}×</span></span>
                  <span style={{ textAlign:'right' }}><strong>{fmtUSD(s.custo)}</strong><br/><span style={{ color:'#46714f', fontSize:'0.74rem' }}>{fmtBRL(s.custo, cot)}</span></span>
                </div>
              ))}
            </div>
            <div style={{ flex:'1 1 280px', background:'#ffffff', border:'1.5px solid #ebe3da', borderRadius:'14px', padding:'1rem 1.25rem' }}>
              <p style={{ margin:'0 0 0.75rem', fontSize:'0.9rem', fontWeight:600, color:'#2f2a26' }}>Top pacientes (mês)</p>
              {data.top_pacientes.length === 0 && <p style={{ color:'#a39b93', fontSize:'0.83rem' }}>Sem dados ainda.</p>}
              {data.top_pacientes.map((p, i) => (
                <div key={i} style={{ display:'flex', justifyContent:'space-between', fontSize:'0.83rem', padding:'0.3rem 0', borderBottom:'1px solid #f6f0ea' }}>
                  <span style={{ overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', maxWidth:'60%' }}>{emo(p.nome)}</span>
                  <span style={{ textAlign:'right' }}><strong>{fmtUSD(p.custo)}</strong><br/><span style={{ color:'#46714f', fontSize:'0.74rem' }}>{fmtBRL(p.custo, cot)}</span></span>
                </div>
              ))}
            </div>
          </div>

          {/* Tabela recentes */}
          <div style={{ background:'#ffffff', border:'1.5px solid #ebe3da', borderRadius:'14px', padding:'1rem 1.25rem', marginBottom:'1.25rem', overflowX:'auto' }}>
            <p style={{ margin:'0 0 0.75rem', fontSize:'0.9rem', fontWeight:600, color:'#2f2a26' }}>Últimos requests</p>
            <table style={{ width:'100%', borderCollapse:'collapse', fontSize:'0.8rem', minWidth:'520px' }}>
              <thead>
                <tr style={{ color:'#736b64', textAlign:'left' }}>
                  <th style={{ padding:'0.35rem 0.5rem' }}>Data/Hora</th>
                  <th style={{ padding:'0.35rem 0.5rem' }}>Serviço</th>
                  <th style={{ padding:'0.35rem 0.5rem' }}>Modelo</th>
                  <th style={{ padding:'0.35rem 0.5rem' }}>Paciente</th>
                  <th style={{ padding:'0.35rem 0.5rem', textAlign:'right' }}>Tokens</th>
                  <th style={{ padding:'0.35rem 0.5rem', textAlign:'right' }}>Custo (US$)</th>
                  <th style={{ padding:'0.35rem 0.5rem', textAlign:'right' }}>Custo (R$)</th>
                </tr>
              </thead>
              <tbody>
                {data.recentes.length === 0 && (
                  <tr><td colSpan={7} style={{ padding:'0.75rem 0.5rem', color:'#a39b93' }}>Nenhum uso registrado ainda.</td></tr>
                )}
                {data.recentes.map((r, i) => (
                  <tr key={i} style={{ borderTop:'1px solid #f6f0ea' }}>
                    <td style={{ padding:'0.35rem 0.5rem', whiteSpace:'nowrap' }}>{fmtDate(r.created_at)}</td>
                    <td style={{ padding:'0.35rem 0.5rem' }}>{emo(SERVICE_LABELS[r.service] || r.service)}</td>
                    <td style={{ padding:'0.35rem 0.5rem' }}>{r.model}</td>
                    <td style={{ padding:'0.35rem 0.5rem', maxWidth:'140px', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{r.paciente}</td>
                    <td style={{ padding:'0.35rem 0.5rem', textAlign:'right' }}>{r.tokens.toLocaleString('pt-BR')}</td>
                    <td style={{ padding:'0.35rem 0.5rem', textAlign:'right', fontWeight:600 }}>{fmtUSD(r.custo)}</td>
                    <td style={{ padding:'0.35rem 0.5rem', textAlign:'right', fontWeight:600, color:'#46714f' }}>{fmtBRL(r.custo, cot)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Configurações de limite */}
          <div style={{ background:'#ffffff', border:'1.5px solid #e3c48a', borderRadius:'14px', padding:'1.25rem 1.5rem' }}>
            <p style={{ margin:'0 0 1rem', fontSize:'0.95rem', fontWeight:600, color:'#2f2a26' }}><Ico e="⚙" sp />Limites e alertas</p>
            <div style={{ marginBottom:'0.9rem' }}>
              <label style={{ display:'block', fontSize:'0.82rem', color:'#4a4440', marginBottom:'0.3rem' }}>Limite de alerta mensal ($)</label>
              <input type="number" min="0" step="1" value={limit} onChange={e => setLimit(e.target.value)}
                style={{ width:'160px', padding:'0.5rem 0.75rem', border:'1.5px solid #d9d0c6', borderRadius:'8px', fontSize:'0.9rem' }} />
            </div>
            <div style={{ marginBottom:'0.9rem' }}>
              <label style={{ display:'block', fontSize:'0.82rem', color:'#4a4440', marginBottom:'0.3rem' }}>Alerta de custo diário acima de ($)</label>
              <input type="number" min="0" step="0.5" value={dailyThr} onChange={e => setDailyThr(e.target.value)}
                style={{ width:'160px', padding:'0.5rem 0.75rem', border:'1.5px solid #d9d0c6', borderRadius:'8px', fontSize:'0.9rem' }} />
            </div>
            <div style={{ marginBottom:'1rem' }}>
              <label style={{ display:'block', fontSize:'0.82rem', color:'#4a4440', marginBottom:'0.3rem' }}>Cotação do dólar (R$)</label>
              <input type="number" min="0" step="0.01" value={cotacao} onChange={e => setCotacao(e.target.value)}
                style={{ width:'160px', padding:'0.5rem 0.75rem', border:'1.5px solid #d9d0c6', borderRadius:'8px', fontSize:'0.9rem' }} />
              <span style={{ marginLeft:'0.6rem', fontSize:'0.76rem', color:'#736b64' }}>Atualize manualmente · usado para converter os custos em reais</span>
            </div>
            <label style={{ display:'flex', alignItems:'center', gap:'0.5rem', fontSize:'0.85rem', color:'#4a4440', marginBottom:'0.6rem', cursor:'pointer' }}>
              <input type="checkbox" checked={pause} onChange={e => setPause(e.target.checked)}
                style={{ width:'auto', flexShrink:0, margin:0 }} />
              <span>Pausar análise de exames se o limite mensal for atingido</span>
            </label>
            <label style={{ display:'flex', alignItems:'center', gap:'0.5rem', fontSize:'0.85rem', color:'#4a4440', marginBottom:'1rem', cursor:'pointer' }}>
              <input type="checkbox" checked={notify} onChange={e => setNotify(e.target.checked)}
                style={{ width:'auto', flexShrink:0, margin:0 }} />
              <span>Notificar por WhatsApp quando o custo diário ultrapassar o limite acima</span>
            </label>
            <button onClick={saveCfg} disabled={savingCfg}
              style={{ padding:'0.6rem 1.5rem', background: BRAND, color:'#ffffff', border:'none', borderRadius:'9px', cursor:'pointer', fontWeight:600, fontSize:'0.875rem' }}>
              {emo(savingCfg ? 'Salvando...' : savedCfg ? '✓ Salvo!' : 'Salvar configurações')}
            </button>
          </div>
        </>
      )}
    </div>
  );
}

// ── Componente Principal ───────────────────────────────────────────────────────
const ADMIN_PANELS = {
  bot: Bot,
  nutricionistas: Nutricionistas,
  substituicoes: Substituicoes,
  cardapios: CardapiosProntos,
  'distribuicao-refeicoes': DistribuicaoRefeicoes,
};

export default function Configuracoes({ onNavigate, isAdmin = false }) {
  const initialBot = new URLSearchParams(window.location.search).get('aba') === 'fluxo-bot';
  return (
    <div style={{ maxWidth: 1120 }}>
      <h1><Ico e="⚙" sp />Configurações</h1>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
        <SettingsPanel icon="🤖" title="Fluxo do Bot" desc="Mensagens, WhatsApp e validação de comprovantes PIX." initiallyOpen={initialBot}>
          {close => <BotFlow onBack={close} />}
        </SettingsPanel>
        {isAdmin && NAV_SECOES.map(({ view, icon, title, desc }) => {
          const Content = ADMIN_PANELS[view];
          return <SettingsPanel key={view} icon={icon} title={title} desc={desc}>
            {close => <Content onNavigate={(target, ...args) => target === 'configuracoes' ? close() : onNavigate(target, ...args)} />}
          </SettingsPanel>;
        })}
        {isAdmin && <SettingsPanel icon="🏥" title="Consultório" desc="Valores, horários, metas e notificações.">
          {close => <ConsultorioModal inline onClose={close} onSaved={() => {}} />}
        </SettingsPanel>}
        <MinhaMarcaCard />
        {isAdmin && <SettingsPanel icon="💾" title="Backup do Banco de Dados" desc="Histórico e geração de backups.">
          <BackupCard />
        </SettingsPanel>}
        <EmailSmtpCard />
        {isAdmin && <SettingsPanel icon="🔍" title="Diagnóstico do Sistema" desc="Serviços, recursos do servidor e logs.">
          {close => <DiagnosticoView onBack={close} />}
        </SettingsPanel>}
        {isAdmin && <SettingsPanel icon="💰" title="Custos IA (OpenAI)" desc="Cobrança do projeto, estimativas locais e alertas.">
          {close => <CustosIAView onBack={close} />}
        </SettingsPanel>}
      </div>
    </div>
  );
}
